import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { isRealEventPoster } from "@tih/shared";
import { EventHubProvider, useEventHub } from "../../../context/EventHubContext";
import { adminFetchLive } from "../../../api/client";
import {
  formatEventWhen,
  phaseBadgeClass,
  phaseLabel,
} from "../../../lib/event-admin-utils";

const TABS = [
  { to: "overview", label: "Overview" },
  { to: "stream", label: "Stream" },
  { to: "gallery", label: "Album" },
  { to: "attendees", label: "Registration" },
  { to: "rsvps", label: "RSVPs" },
  { to: "blog", label: "Blog" },
] as const;

export function AdminEventHubLayout() {
  return (
    <EventHubProvider>
      <AdminEventHubShell />
    </EventHubProvider>
  );
}

function AdminEventHubShell() {
  const hub = useEventHub();
  const base = `/admin/events/${hub.eventId}`;
  const [onAir, setOnAir] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminFetchLive()
      .then((live) => {
        if (cancelled) return;
        const currentId = live.events.find((e) => e.streamRole === "current")?.id;
        setOnAir(Boolean(live.info.isLive && currentId === hub.eventId));
      })
      .catch(() => {
        if (!cancelled) setOnAir(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hub.eventId, hub.summary?.hasStream]);

  if (hub.loading) {
    return <div className="admin-page" style={{ opacity: 0.5 }}>Loading event…</div>;
  }

  if (hub.error || !hub.event) {
    return (
      <div className="admin-page">
        <div className="form-error">{hub.error ?? "Event not found"}</div>
        <Link to="/admin/events" className="btn btn-ghost">
          Back to events
        </Link>
      </div>
    );
  }

  const ev = hub.event;
  const sum = hub.summary;
  const poster = isRealEventPoster(ev.posterUrl) ? ev.posterUrl : null;

  return (
    <div className="admin-page">
      <header className="admin-event-hub-header admin-card">
        <div className="admin-event-hub-header-main">
          {poster ? (
            <img src={poster} alt="" className="admin-event-hub-poster" />
          ) : (
            <div className="admin-event-hub-poster admin-event-hub-poster--empty" aria-hidden />
          )}
          <div>
            <Link to="/admin/events" className="admin-event-hub-back">
              ← All events
            </Link>
            <h1 className="admin-event-hub-title">{hub.displayTitle}</h1>
            <p className="admin-muted">
              {formatEventWhen(ev.startsAt)} → {formatEventWhen(ev.endsAt)}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {hub.phase && (
                <span className={phaseBadgeClass(hub.phase)}>{phaseLabel(hub.phase)}</span>
              )}
              {hub.featuredId === ev.id && (
                <span className="admin-badge admin-badge-featured">On public home</span>
              )}
              {onAir && (
                <span className="admin-badge admin-badge-live">On air</span>
              )}
            </div>
            {sum && (
              <p className="admin-event-hub-stats">
                {sum.rsvpCount} RSVP{sum.rsvpCount === 1 ? "" : "s"}
                {sum.newRsvpCount > 0 && ` (${sum.newRsvpCount} new)`}
                {" · "}
                {sum.checkedInCount}/{sum.attendeeCount} checked in
                {" · "}
                Album: {sum.hasAlbum ? `${sum.albumPhotoCount} photos` : "none"}
                {sum.hasStream && " · Stream set"}
                {sum.blogPostCount > 0 && ` · ${sum.blogPostCount} blog post${sum.blogPostCount === 1 ? "" : "s"}`}
              </p>
            )}
          </div>
        </div>
      </header>

      <nav className="admin-tabs" aria-label="Event sections">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={`${base}/${tab.to}`}
            className={({ isActive }) => (isActive ? "active" : undefined)}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
